import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@/shared/ui'
import { USER_ROLE } from '@/shared/api'

const ROLE_OPTIONS = [
  { value: USER_ROLE.COURIER, label: 'Штатный' },
  { value: USER_ROLE.EXTERNAL_COURIER, label: 'Сторонний' },
] as const

interface ApproveModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (role: number, comment: string) => void | Promise<void>
  courierName?: string
  currentRole?: number | undefined
  loading?: boolean
}

export const ApproveModal: React.FC<ApproveModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  courierName,
  currentRole,
  loading = false,
}) => {
  const getDefaultRole = () =>
    currentRole === USER_ROLE.COURIER || currentRole === USER_ROLE.EXTERNAL_COURIER
      ? currentRole
      : USER_ROLE.COURIER

  const [role, setRole] = useState<number>(getDefaultRole())
  const [comment, setComment] = useState('Одобрен администратором')

  useEffect(() => {
    if (isOpen) {
      setRole(getDefaultRole())
      setComment('Одобрен администратором')
    }
  }, [isOpen, currentRole])

  if (!isOpen) return null

  const handleSubmit = () => {
    void onSubmit(role, comment.trim())
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Одобрить курьера{courierName ? ` «${courierName}»` : ''}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Тип курьера */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Тип курьера
            </label>
            <select
              value={role}
              onChange={(e) => setRole(Number(e.target.value))}
              className="admin-input w-full"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Тип можно изменить и после одобрения в карточке курьера
            </p>
          </div>

          {/* Комментарий */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Комментарий
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Комментарий к решению..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={loading}
          >
            Одобрить
          </Button>
        </div>
      </div>
    </div>
  )
}
